/**
 * **Terrain mechanics regions** (`projects/terrain-mechanics-overlay.md`): the painted terrain
 * grouped into connected pieces that share mechanics, each with the words that name them. The
 * board's terrain mechanics layer draws them and `lvtt encounter terrain regions` lists them, so
 * both read this one answer.
 *
 * Also home to {@link tilePropertyList}, the chip words and hover sentences for a property bag,
 * so the CLI prints the same words the palette, the Map key and the TILE pane do. The sentences
 * are rules claims; the frontend's `terrainTiles.ts` header checks each against the book, and the
 * rules-copy registry sweeps them.
 *
 * Mechanics come from the properties, never from the tile id (`decisions/0185`): a region is keyed
 * on {@link terrainPropertiesKey}, so a custom cell joins the catalogue tiles with the same set.
 */
import { parseSquareKey, square, squareKey } from "@lancer-vtt/space";
import { DEFAULT_HAZARD_TYPE, terrainPropertiesKey, terrainPropertyEntries, type TerrainMap, type TerrainProperties } from "./terrain";

/** One mechanical property a tile can carry, in the order the chips and sentences list them. */
export type TilePropertyId = "impassable" | "blocks-flight" | "blocks-sight" | "difficult" | "soft-cover" | "hard-cover" | "dangerous";

/** One property of a tile, as a short chip and as a plain-words sentence. */
export type TileProperty = {
  id: TilePropertyId;
  /** The chip on a palette button or a legend row, e.g. `"soft cover"`, `"dangerous: burn"`. */
  chip: string;
  /** One sentence for the hover pane, e.g. `"Difficult terrain: costs 2 spaces of movement to enter."`. */
  line: string;
};

/**
 * Every property `properties` carries, in a fixed order: movement first, then sight, then what it
 * costs to cross, then cover, then harm. An empty bag (open ground) has none.
 */
export function tilePropertyList(properties: TerrainProperties): TileProperty[] {
  const out: TileProperty[] = [];
  const flightBlocked = properties.blocksMovement === true && properties.blocksFlight === true;
  if (properties.blocksMovement === true) {
    out.push({
      id: "impassable",
      chip: "impassable",
      line: flightBlocked
        ? "Impassable: ground movement can't enter it."
        : "Impassable: ground movement can't enter it. A flyer crosses over, but can't end its move here.",
    });
  }
  if (flightBlocked) {
    out.push({ id: "blocks-flight", chip: "blocks flight", line: "Blocks flight: a flyer can't cross it either — it runs floor to ceiling." });
  }
  if (properties.blocksSight === true) {
    out.push({ id: "blocks-sight", chip: "blocks sight", line: "Blocks sight: nothing sees or shoots through it." });
  }
  const cost = properties.moveCost ?? 1;
  if (cost >= 2) {
    out.push({ id: "difficult", chip: "difficult", line: `Difficult terrain: costs ${cost} spaces of movement to enter.` });
  }
  if (properties.cover === "soft") {
    out.push({ id: "soft-cover", chip: "soft cover", line: "Soft cover: +1 Difficulty on ranged attacks against a target in it or behind it." });
  }
  if (properties.cover === "hard") {
    out.push({
      id: "hard-cover",
      chip: "hard cover",
      line: "Hard cover: +2 Difficulty on ranged attacks against a target sheltering behind it and adjacent to it. A target standing in it, or further back, gets soft cover instead.",
    });
  }
  if (properties.dangerous === true) {
    const type = properties.hazard ?? DEFAULT_HAZARD_TYPE;
    out.push({
      id: "dangerous",
      chip: `dangerous: ${type}`,
      line: `Dangerous terrain (${type}): entering it for the first time in a round, or ending a turn in it, forces an ENGINEERING check (target number 10) — 5 ${type} damage on a failure.`,
    });
  }
  return out;
}

/** The short form of each property for a region too small on screen to hold the full label. */
function compactChip(p: TileProperty): string {
  switch (p.id) {
    case "impassable":
      return "IMP";
    case "blocks-flight":
      return "NO FLY";
    case "blocks-sight":
      return "NO LOS";
    case "difficult":
      return "DIF";
    case "soft-cover":
      return "SOFT";
    case "hard-cover":
      return "HARD";
    case "dangerous":
      return `DGR:${p.chip.slice("dangerous: ".length).toUpperCase()}`;
  }
}

/**
 * A region's label: its chips upper-cased and joined with " · " (`DIFFICULT · SOFT COVER`), or the
 * compact form (`DIF·SOFT`) for a region too small on screen for the full one.
 */
export function terrainRegionLabel(properties: TileProperty[], opts: { compact?: boolean } = {}): string {
  if (opts.compact === true) return properties.map(compactChip).join("·");
  return properties.map((p) => p.chip.toUpperCase()).join(" · ");
}

/** How cells group: one region per distinct property set, or one layer per property. */
export type TerrainRegionBasis = "set" | "property";

/** One 8-connected piece of cells that share a grouping key. */
export type TerrainRegion = {
  /** The grouping key: a {@link terrainPropertiesKey} (`set`), or a property id (`property`), e.g. `dangerous:burn`. */
  key: string;
  /** What the piece's label names: every property of the set, or the one property of the layer. */
  properties: TileProperty[];
  /** Its cells as `"x,y"` keys, row by row. */
  cells: string[];
  /** The most interior cell, where the label sits. */
  labelCell: string;
};

/** A property's layer key under `by: "property"`; a dangerous layer is split by its damage type. */
function propertyLayerKey(p: TileProperty): string {
  return p.id === "dangerous" ? `dangerous:${p.chip.slice("dangerous: ".length)}` : p.id;
}

/**
 * The painted terrain grouped into 8-connected pieces. `by: "set"` groups cells whose normalized
 * property sets are equal, whatever their tile; `by: "property"` puts each cell in one layer per
 * property it has, so a cell can sit in several pieces. Open ground, a cell whose properties are
 * empty, and off-board cells are skipped. Pieces are ordered by key, then by their first cell.
 */
export function terrainMechanicsRegions(terrain: TerrainMap, board: { width: number; height: number }, opts: { by: TerrainRegionBasis }): TerrainRegion[] {
  const groups = new Map<string, { properties: TileProperty[]; cells: Set<number> }>();
  const add = (key: string, properties: TileProperty[], index: number): void => {
    let g = groups.get(key);
    if (g === undefined) {
      g = { properties, cells: new Set() };
      groups.set(key, g);
    }
    g.cells.add(index);
  };
  for (const [k, properties] of terrainPropertyEntries(terrain)) {
    const { x, y } = parseSquareKey(k);
    if (!(x >= 0 && y >= 0 && x < board.width && y < board.height)) continue;
    const list = tilePropertyList(properties);
    if (list.length === 0) continue;
    const index = y * board.width + x;
    if (opts.by === "set") add(terrainPropertiesKey(properties), list, index);
    else for (const p of list) add(propertyLayerKey(p), [p], index);
  }

  const out: TerrainRegion[] = [];
  for (const [key, g] of groups) {
    for (const piece of connectedPieces(g.cells, board.width)) {
      out.push({ key, properties: g.properties, cells: piece.map((i) => cellKey(i, board.width)), labelCell: cellKey(labelIndex(piece, board.width), board.width) });
    }
  }
  const first = (r: TerrainRegion): number => {
    const { x, y } = parseSquareKey(r.cells[0]!);
    return y * board.width + x;
  };
  return out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : first(a) - first(b)));
}

function cellKey(index: number, width: number): string {
  return squareKey(square(index % width, Math.floor(index / width)));
}

/** `cells` (board indices) split into 8-connected pieces, each sorted row by row. */
function connectedPieces(cells: Set<number>, width: number): number[][] {
  const seen = new Set<number>();
  const pieces: number[][] = [];
  for (const start of [...cells].sort((a, b) => a - b)) {
    if (seen.has(start)) continue;
    const piece: number[] = [];
    const stack = [start];
    seen.add(start);
    while (stack.length > 0) {
      const i = stack.pop()!;
      piece.push(i);
      const x = i % width;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if ((dx === 0 && dy === 0) || x + dx < 0 || x + dx >= width) continue;
          const n = i + dy * width + dx;
          if (cells.has(n) && !seen.has(n)) {
            seen.add(n);
            stack.push(n);
          }
        }
      }
    }
    pieces.push(piece.sort((a, b) => a - b));
  }
  return pieces;
}

/**
 * The piece's most interior cell: the greatest Chebyshev distance to a cell outside the piece,
 * ties to the first row, then the first column. A two-pass chamfer over the piece's bounding box
 * padded by one outside cell, which is exact for Chebyshev distance.
 */
function labelIndex(piece: number[], width: number): number {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const i of piece) {
    const x = i % width;
    const y = Math.floor(i / width);
    x0 = Math.min(x0, x);
    x1 = Math.max(x1, x);
    y0 = Math.min(y0, y);
    y1 = Math.max(y1, y);
  }
  const w = x1 - x0 + 3;
  const h = y1 - y0 + 3;
  const d = new Array<number>(w * h).fill(0);
  for (const i of piece) d[(Math.floor(i / width) - y0 + 1) * w + (i % width) - x0 + 1] = Infinity;
  const relax = (at: number, from: number): void => {
    if (d[from]! + 1 < d[at]!) d[at] = d[from]! + 1;
  };
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const at = y * w + x;
      relax(at, at - 1);
      relax(at, at - w - 1);
      relax(at, at - w);
      relax(at, at - w + 1);
    }
  }
  for (let y = h - 2; y >= 1; y--) {
    for (let x = w - 2; x >= 1; x--) {
      const at = y * w + x;
      relax(at, at + 1);
      relax(at, at + w + 1);
      relax(at, at + w);
      relax(at, at + w - 1);
    }
  }
  let best = piece[0]!;
  let bestD = -1;
  for (const i of piece) {
    const v = d[(Math.floor(i / width) - y0 + 1) * w + (i % width) - x0 + 1]!;
    if (v > bestD) {
      best = i;
      bestD = v;
    }
  }
  return best;
}
